// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import Select from '@dtci/spellbook/Select';
import Typography from '@dtci/spellbook/Typography';
// --- API
import { datastageApi } from '../../../api';
// --- Actions
import { useProductsActions } from '../../../store/products/useProducts';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';

/**
 * CopyClearanceMemoForm Component
 *
 * This component renders a form for copying a clearance memo to other products.
 * It includes a select input for choosing products to copy to.
 * It uses the Select component for product selection.
 *
 * @component
 * @param {Object} data - The data object containing product information.
 */
const CopyClearanceMemoForm = ({ data }) => {
    const form = useDialogsForm();
    const { productOptions } = useSelector((state) => state.products);
    const { updateProducts } = useProductsActions();

    // Function to handle the change event of the select input
    const handleChange = useCallback((value) => form.setValue('copyTo', value), [form]);

    // Function to load options for the select input
    const loadOptions = useCallback(
        async (inputValue, callback) => {
            if (inputValue.length <= 2) {
                return callback([]);
            }

            try {
                // Fetch products from the API based on the input value
                const response = await datastageApi.productsToCopyTo(
                    { hasClearanceMemo: false, q: inputValue },
                    [0, 9],
                    ['productTitle', 'ASC'],
                );

                // Map the response to create options for the select input
                const options =
                    response?.content?.map((x) => ({
                        label: x.groupTitle,
                        value: x.id,
                    })) || [];

                // Update the product option
                updateProducts({ productOptions: options });

                // Call the callback function with the options
                callback(options);
            } catch (error) {
                console.error('Error loading options:', error);
                callback([]);
            }
        },
        [updateProducts],
    );

    return (
        <div className="flexBox">
            <Typography variant="T20" bold>
                {data.title} ({data.productId})
            </Typography>
            <Select
                async
                isSearchable
                useMenuPortal
                name="copyTo"
                placeholder="Select products to copy to..."
                defaultOptions={productOptions}
                value={form.state.formData?.copyTo}
                onChange={handleChange}
                loadOptions={loadOptions}
            />
        </div>
    );
};

/**
 * useCopyToDialog Hook
 *
 * This hook provides functionality to open the copy to dialog for clearance memos.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openCopyToDialog function.
 */
export const useCopyToDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => () => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the copy to dialog
    const openCopyToDialog = useCallback(
        (data, onConfirm) =>
            openDialog({
                title: 'Copy Clearance Memo',
                content: <CopyClearanceMemoForm data={data} />,
                closeAction: true,
                primaryAction: {
                    label: 'Save',
                    action: handleSubmit(onConfirm),
                },
            }),
        [openDialog, handleSubmit],
    );

    return { openCopyToDialog };
};
